// Persisted state schema (v3). Everything read from storage is validated; anything invalid is
// dropped field by field rather than white-screening the app. v2 blobs parse into v3 because every
// field added in v3 has a default.

import { z } from 'zod';
import { isDietRuleId, type DietRuleId } from '../domain/diet';
import { isIngredientId, type IngredientId } from '../domain/taxonomy';
import { getRecipe } from '../domain/recipes';
import { CUISINES } from '../domain/catalog/schema';
import type { CookingStyle } from '../domain/ranking';
import { TIME_CHOICES } from '../domain/preferences';

export const STATE_VERSION = 3;

const ingredientId = z.string().refine(isIngredientId) as unknown as z.ZodType<IngredientId>;
const dietRuleId = z.string().refine(isDietRuleId) as unknown as z.ZodType<DietRuleId>;
const recipeId = z.string().refine((id) => !!getRecipe(id));
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
// Every other string is bounded too: synced state comes back from the server, so nothing in it is
// trusted to be the size the app wrote.
const stamp = z.string().max(40);
const shortId = z.string().max(80);

/** Parse an array, silently dropping invalid elements. */
const lenientArray = <T extends z.ZodTypeAny>(item: T, max = 500) =>
  z
    .array(z.unknown())
    .catch([])
    .transform((xs) => xs.slice(0, max).flatMap((x) => {
      const r = item.safeParse(x);
      return r.success ? [r.data as z.infer<T>] : [];
    }));

/** Use `fallback` when the field is absent (older saves), otherwise parse leniently. */
const withDefault = <T extends z.ZodTypeAny>(schema: T, fallback: unknown) =>
  z.preprocess((v) => (v === undefined ? fallback : v), schema);

const STYLES = ['quick', 'easy_only', 'one_pot', 'budget', 'high_protein'] as const satisfies readonly CookingStyle[];
const EQUIPMENT = ['stovetop', 'oven', 'blender', 'toaster'] as const;
const GOALS = ['maintain', 'gain', 'lose', 'muscle', 'performance', 'quality', 'balanced', 'pattern', 'none'] as const;
const PRIORITIES = ['convenience', 'variety', 'nutrition', 'affordability', 'less_waste', 'fitness'] as const;
const FRUSTRATIONS = ['what_to_cook', 'waste', 'cost', 'time', 'goals', 'boredom'] as const;
const LIKES = ['meat', 'rice', 'pasta_noodles', 'seafood', 'vegetarian', 'african_caribbean', 'asian', 'mediterranean', 'comfort', 'international', 'unsure'] as const;
const EXERCISE = ['strength', 'cardio', 'sport', 'walking', 'yoga'] as const;

export const profileSchema = z.object({
  /** What Amari calls them (asked in the welcome chat). Optional; cleaned and capped on input. */
  name: z.string().max(40).catch(''),
  // Hard rules: enforced in code, never by a model.
  rules: lenientArray(dietRuleId, 40),
  /** Free-text "other" allergies the taxonomy can't enforce; shown as a label-check reminder. */
  otherAllergies: z.string().max(200).catch(''),
  dislikes: lenientArray(ingredientId, 100),
  /** Recipes the user said never to suggest. */
  hiddenRecipes: lenientArray(recipeId, 200),
  staples: lenientArray(ingredientId, 100),
  // Food preferences
  /** What makes cooking hard for them (onboarding); shapes ranking and messaging. */
  frustrations: lenientArray(z.enum(FRUSTRATIONS), 6),
  /** Kinds of food they enjoy (onboarding); boosts matching recipes. */
  likes: lenientArray(z.enum(LIKES), 11),
  styles: lenientArray(z.enum(STYLES), 10),
  cuisines: lenientArray(z.enum(CUISINES), CUISINES.length),
  heat: z.enum(['none', 'mild', 'hot']).catch('mild'),
  skill: z.enum(['new', 'comfortable', 'confident']).catch('comfortable'),
  maxMinutes: z.number().refine((n) => (TIME_CHOICES as readonly number[]).includes(n)).catch(40),
  // Kitchen and schedule
  equipment: withDefault(lenientArray(z.enum(EQUIPMENT), 4), ['stovetop', 'oven', 'toaster']),
  shopping: z.enum(['weekly', 'twice_weekly', 'as_needed']).catch('weekly'),
  budget: z.enum(['tight', 'moderate', 'flexible']).catch('moderate'),
  household: z.number().int().min(1).max(12).catch(2),
  nights: z.number().int().min(1).max(7).catch(5),
  meals: withDefault(lenientArray(z.enum(['dinner', 'breakfast']), 2), ['dinner']),
  priorities: lenientArray(z.enum(PRIORITIES), 3),
  // Goals and optional wellness data
  goals: lenientArray(z.enum(GOALS), 6),
  activity: z.enum(['light', 'moderate', 'active']).nullable().catch(null),
  exercise: lenientArray(z.enum(EXERCISE), 5),
  units: z.enum(['metric', 'imperial']).catch('metric'),
  /** Hide all nutrition and body numbers everywhere. */
  hideNumbers: z.boolean().catch(false),
  /** Whether the app may learn from behaviour (ratings, skips, swaps). */
  learning: z.boolean().catch(true),
  trackWeight: z.boolean().catch(false),
  checkInMonths: z.union([z.literal(0), z.literal(1), z.literal(3), z.literal(6)]).catch(3),
  /**
   * Reminders (native app local notifications). Off until the person turns them on and allows them;
   * then dinner time, shopping day and cooking timers are on, the rest opt-in.
   */
  notifications: z.object({
    enabled: z.boolean().catch(false),
    dinner: z.boolean().catch(true),
    dinnerTime: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).catch('16:30'),
    dinnerEveryDay: z.boolean().catch(false),
    shop: z.boolean().catch(true),
    useSoon: z.boolean().catch(false),
    planNext: z.boolean().catch(false),
    checkIn: z.boolean().catch(false),
    timers: z.boolean().catch(true),
    quietStart: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).catch('21:00'),
    quietEnd: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/).catch('08:00'),
    /** When the in-app "turn on reminders?" card was answered, so it isn't shown again. */
    askedAt: stamp.nullable().catch(null),
  }).catch(() => ({ enabled: false, dinner: true, dinnerTime: '16:30', dinnerEveryDay: false, shop: true, useSoon: false, planNext: false, checkIn: false, timers: true, quietStart: '21:00', quietEnd: '08:00', askedAt: null })),
  /** Occasional in-app questions that refine the profile. */
  prompts: z.object({
    enabled: z.boolean().catch(true),
    /** Prompt id -> ISO time it was answered or dismissed; either way it is not asked again. */
    done: z.record(shortId, stamp).catch({}),
    lastShownAt: stamp.nullable().catch(null),
  }).catch({ enabled: true, done: {}, lastShownAt: null }),
});

const unitSchema = z.enum(['g', 'ml', 'cup', 'tbsp', 'tsp', 'each', 'clove', 'slice', 'can', 'pinch', 'handful']);
/** How much of an item the user has, only when they entered it. */
export const amountSchema = z.object({ amount: z.number().nonnegative().max(100_000), unit: unitSchema });

export const kitchenItemSchema = z.object({
  key: shortId,
  ingredientId,
  useSoon: z.boolean().catch(false),
  addedAt: stamp,
  source: z.enum(['scan', 'manual', 'sample', 'list']).catch('manual'),
  amount: amountSchema.nullable().catch(null),
});

const skipReason = z.enum(['no_time', 'not_in_mood', 'missing_ingredient', 'ate_out', 'didnt_fancy', 'other']);
export const planEntrySchema = z.object({
  date: isoDate,
  recipeId,
  servings: z.number().int().min(1).max(12),
  locked: z.boolean().catch(false),
  status: z.enum(['planned', 'cooked', 'skipped']).catch('planned'),
  skipReason: skipReason.nullable().catch(null),
});

export const planSchema = z.object({
  startDate: isoDate,
  entries: lenientArray(planEntrySchema, 14),
  createdAt: stamp,
  /** The suggestions the user picked from, for evaluation. */
  offered: lenientArray(shortId, 20).catch([]),
});

export const listItemSchema = z.object({
  key: z.string().max(80),
  ingredientId: ingredientId.nullable(),
  label: z.string().max(80),
  qty: lenientArray(z.object({ amount: z.number().nonnegative(), unit: unitSchema }), 10),
  aisle: z.enum(['produce', 'meat_seafood', 'dairy_eggs', 'bakery', 'pantry', 'spices', 'condiments', 'frozen', 'other']),
  checked: z.boolean().catch(false),
  source: z.enum(['plan', 'manual']).catch('manual'),
  recipeIds: lenientArray(shortId, 20),
});

export const cookedSchema = z.object({
  recipeId,
  at: stamp,
  rating: z.enum(['up', 'down']).nullable().catch(null),
  /** Optional quick feedback after cooking. */
  notes: lenientArray(z.enum(['too_long', 'too_spicy', 'too_bland', 'too_much_work', 'loved_it', 'big_hit']), 6).catch([]),
  /** How long it took relative to the recipe's estimate. */
  took: z.enum(['quicker', 'about_right', 'longer']).nullable().catch(null),
  fromPlan: z.boolean().catch(false),
});

/** Behavioural signals the learner reads. Capped and pruned; never sent anywhere. */
export const feedbackSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('not_today'), recipeId: shortId, reason: z.enum(['not_in_mood', 'too_long', 'dont_like', 'too_spicy', 'missing']).nullable().catch(null), at: stamp }),
  z.object({ type: z.literal('skip'), recipeId: shortId, reason: skipReason.nullable().catch(null), at: stamp }),
  z.object({ type: z.literal('swap'), from: shortId, to: shortId, at: stamp }),
  z.object({ type: z.literal('chose'), recipeId: shortId, surface: z.enum(['alternatives', 'almost', 'plan']), at: stamp }),
  z.object({ type: z.literal('passed'), recipeIds: lenientArray(shortId, 20), surface: z.literal('plan'), at: stamp }),
  z.object({ type: z.literal('wasted'), ingredientId, at: stamp }),
  z.object({ type: z.literal('used_up'), ingredientId, at: stamp }),
]);

export const weightSchema = z.object({ date: isoDate, kg: z.number().min(25).max(350) });

export const checkInSchema = z.object({
  at: stamp,
  satisfaction: z.enum(['great', 'ok', 'not_really']).nullable().catch(null),
  changed: lenientArray(z.string().max(40), 10).catch([]),
});

export const persistedSchema = z.object({
  version: z.literal(STATE_VERSION),
  onboarded: z.boolean().catch(false),
  onboardedAt: stamp.nullable().catch(null),
  consent: z.object({ analytics: z.boolean(), decidedAt: stamp.nullable() }).catch({ analytics: false, decidedAt: null }),
  profile: profileSchema.catch(() => profileSchema.parse({})),
  kitchen: z.object({ items: lenientArray(kitchenItemSchema, 200), updatedAt: stamp.nullable() }).catch({ items: [], updatedAt: null }),
  plan: planSchema.nullable().catch(null),
  /** Finished plans, newest first; the learner reads adherence and skips from these. */
  planHistory: lenientArray(planSchema, 12).catch([]),
  list: lenientArray(listItemSchema, 300),
  saved: lenientArray(recipeId, 300),
  cooked: lenientArray(cookedSchema, 300),
  feedback: lenientArray(feedbackSchema, 500).catch([]),
  /** User decisions about inferred preferences: key -> confirmed/rejected. */
  inferenceOverrides: z.record(shortId, z.enum(['confirmed', 'rejected'])).catch({}),
  weights: lenientArray(weightSchema, 400).catch([]),
  checkIns: lenientArray(checkInSchema, 40).catch([]),
  checkInSnoozedUntil: stamp.nullable().catch(null),
  theme: z.enum(['system', 'light', 'dark']).catch('system'),
  /** Send scrubbed crash reports (on unless turned off in Privacy). */
  crashReports: z.boolean().catch(true),
  /** Free plan: recipes opened in full this week (domain/plans.ts). Synced, so it holds across devices. */
  recipeUnlocks: z.object({ week: z.string().max(10), ids: lenientArray(recipeId, 50) }).catch({ week: '', ids: [] }),
  /** When the "more meals" invitation after the first meal was dismissed, so it isn't repeated. */
  moreMealsDismissedAt: stamp.nullable().catch(null),
  /**
   * When each save or hide was last switched on or off ("saved:<id>", "hidden:<id>"), so a sync
   * merge keeps the latest choice instead of bringing back something removed on another device.
   * The newest 600 are kept.
   */
  toggledAt: z.record(shortId, stamp).catch({})
    .transform((t) => Object.fromEntries(Object.entries(t).sort((a, b) => b[1].localeCompare(a[1])).slice(0, 600))),
});

export type PersistedState = z.infer<typeof persistedSchema>;
export type Profile = z.infer<typeof profileSchema>;
export type KitchenItem = z.infer<typeof kitchenItemSchema>;
export type CookedEntry = z.infer<typeof cookedSchema>;
export type Feedback = z.infer<typeof feedbackSchema>;
export type SkipReason = z.infer<typeof skipReason>;
export type WeightEntry = z.infer<typeof weightSchema>;
export type CheckIn = z.infer<typeof checkInSchema>;
